import React, { useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ATTENTION_COLOR, DISTRIBUTION_TYPE, QR_TYPE, SECTION_TYPE } from '../../utils/Constants';

import styles from './styles.module.css';
import { OrderTicket } from 'types';

const NumberCheck = ({ x, y, size, color }: { x?: number; y?: number; size: number; color: string }) => {
    return (
        <svg x={x} y={y} height={size} width={size} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48.83 48.83">
            <g id="text">
                <g>
                    <circle
                        className="cls-2"
                        cx="24.42"
                        cy="24.42"
                        r="23"
                        fill={`${color}`}
                        stroke={color === ATTENTION_COLOR ? '#000' : '#00000000'}
                        strokeWidth="1"
                    />
                    <polygon
                        fill="#FFFFFF"
                        points="19.47 34.44 10.69 25.17 12.65 23.32 19.64 30.69 37 15.31 38.79 17.32 19.47 34.44"
                    />
                </g>
            </g>
        </svg>
    );
};

const NumberTag = ({
    size = 38,
    textColor,
    name,
    number,
    isCustom,
    unitPerson,
}: {
    size: number;
    textColor: string;
    name: string;
    number: number;
    isCustom: boolean;
    x?: number;
    y?: number;
    unitPerson: string;
}) => {
    return (
        <div className={`${styles.numberTag} customNumber`}>
            <NumberCheck size={size} color={isCustom ? ATTENTION_COLOR : '#555'} />
            <span
                style={{
                    fontSize: size,
                    fontWeight: 'bold',
                    fontFamily: 'NotoSansJP',
                    color: textColor,
                    marginLeft: '5px',
                    whiteSpace: 'nowrap',
                }}
            >
                {`${name} ${number}${unitPerson}`}
            </span>
        </div>
    );
};

/**
 * カスタム人数がある場合、人数の内訳を表示する。
 */
type DisplayCustomNumbersProps = {
    orderTicket: OrderTicket;
    textColor: string;
    circleSize: number;
};

const DisplayCustomNumbers = memo(({ orderTicket, textColor, circleSize }: DisplayCustomNumbersProps) => {
    const { t } = useTranslation();
    const {
        numberAdults,
        numberCustom1,
        numberCustom2,
        numberCustom3,
        custom1Name,
        custom2Name,
        custom3Name,
        distributionType,
        remainingOrderDistribution,
        sectionType,
        qrType,
    } = orderTicket ?? {};

    // テキストの長さに関係なく全てのCustomNumbers要素の幅を揃えて、表示のズレを防ぐため
    useEffect(() => {
        const tags = document.querySelectorAll('.customNumber');
        let maxWidth = 0;

        tags.forEach((tag) => {
            const htmlTag = tag as HTMLElement;
            htmlTag.style.width = 'auto';
            const width = htmlTag.offsetWidth;
            if (width > maxWidth) maxWidth = width;
        });

        tags.forEach((tag) => {
            const htmlTag = tag as HTMLElement;
            htmlTag.style.width = `${maxWidth}px`;
        });
    }, [circleSize]);

    let displayNumberAdults = numberAdults;
    let displayNumberCustom1 = numberCustom1;
    let displayNumberCustom2 = numberCustom2;
    let displayNumberCustom3 = numberCustom3;

    if (distributionType === DISTRIBUTION_TYPE.ENABLE || distributionType === DISTRIBUTION_TYPE.REQUIRED) {
        const { remainingNumberAdults, remainingNumberCustom1, remainingNumberCustom2, remainingNumberCustom3 } =
            remainingOrderDistribution ?? {};
        displayNumberAdults = remainingNumberAdults ?? 0;
        displayNumberCustom1 = remainingNumberCustom1 ?? 0;
        displayNumberCustom2 = remainingNumberCustom2 ?? 0;
        displayNumberCustom3 = remainingNumberCustom3 ?? 0;
    }

    const displayData = (() => {
        const displayData = [];
        // カスタム人数を表示するかどうか
        const shouldDisplayCustomNumbers =
            displayNumberCustom1 > 0 || displayNumberCustom2 > 0 || displayNumberCustom3 > 0;

        if (displayNumberAdults > 0) {
            displayData.push({
                name: t('ticket-detail.adult-label', { lng: 'ja' }),
                number: displayNumberAdults,
                isCustom: false,
                isDisplay:
                    shouldDisplayCustomNumbers || sectionType === SECTION_TYPE.MULTI || qrType === QR_TYPE.SINGLE_SCAN, //多区間予約チケットまたQRチケットの場合は大人のみの場合でも表示
            });
        }
        if (displayNumberCustom1 > 0) {
            displayData.push({
                name: custom1Name?.['ja'],
                number: displayNumberCustom1,
                isCustom: true,
                isDisplay: true,
            });
        }
        if (displayNumberCustom2 > 0) {
            displayData.push({
                name: custom2Name?.['ja'],
                number: displayNumberCustom2,
                isCustom: true,
                isDisplay: true,
            });
        }
        if (displayNumberCustom3 > 0) {
            displayData.push({
                name: custom3Name?.['ja'],
                number: displayNumberCustom3,
                isCustom: true,
                isDisplay: true,
            });
        }
        return displayData;
    })();

    const x = -150;
    const y = 80;

    const dx = 270;
    const dy = 80;
    return (
        <div className={styles.container}>
            {displayData.map((item, index) => {
                if (!item.isDisplay) return null;
                const pdx =
                    index % 2 === 0
                        ? displayData.length % 2 !== 0 && displayData.length === index + 1
                            ? dx / 2
                            : 0
                        : dx;
                const pdy = index >= 2 ? dy : 0;
                return (
                    <NumberTag
                        key={index}
                        x={x + pdx}
                        y={y + pdy}
                        name={item.name ?? ''}
                        number={item.number}
                        isCustom={item.isCustom}
                        textColor={textColor}
                        size={circleSize}
                        unitPerson={t('ticket-display.unit-person', { lng: 'ja' })}
                    />
                );
            })}
        </div>
    );
});

export default DisplayCustomNumbers;
